import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
/* eslint-disable  import/no-cycle */
import set from 'lodash/set';
import { RootState } from 'store/rootReducer';
import { ApiErrors, Loaders } from 'api/types';
import { UNAUTHORIZED, FORBIDDEN, NOT_FOUND, UNPROCESSABLE_ENTITY } from 'http-status-codes';
import { i18n } from 'helpers/i18n';
import { History } from 'history';
import { axiosInstance } from '../axiosInstance';
import moment from 'moment';

type ProductData = {
  customer_item_code: string;
  product_id: string;
  qa_type: string;
};

type OrderListSave = {
  history?: History;
  id?: string;
  detailsOnly?: boolean;
  orderList: {
    customer_id?: string;
    product_data?: ProductData[];
    serial_numbers: string[];
    short_name?: string;
    reference_code?: string;
    customer_reference_code?: string;
    received_at?: string;
    tracking_url?: string;
  };
};

export type OrderListSerialNumber = {
  issue_date: string;
  site_code: string;
  site_country_code: string;
  site_name: string;
  value: string;
};

export type OrderListProduct = {
  customer_item_code?: string;
  description?: string;
  id: string;
  item_code?: string;
  name: string;
  product_id: string;
  qr_code?: string;
  qa_type: string;
  serial_number: OrderListSerialNumber[];
};

export type OrderList = {
  approved_at?: string;
  created_at: string;
  customer_id: string;
  customer_name?: string;
  date: string;
  id: string;
  products: OrderListProduct[];
  short_name: string;
  reference_code: string;
  customer_reference_code: string;
  received_at?: string;
  tracking_url?: string;
};

type OrdersListState = {
  apiErrors: ApiErrors;
  details: OrderList | undefined;
  isSearch?: boolean;
  /* list: {
    [key: string]: OrderList;
  }; */
  loaders: Loaders;
};

export interface UpdateOrdersListPayload {
  isSearch: boolean;
}

const orderListsApiPrefix = '/orderlists';

// TODO: use object instead of  array
const initialState: OrdersListState = {
  apiErrors: {
    loadingError: '',
    savingError: ''
  },
  details: undefined,
  loaders: {
    isLoading: false,
    isSaving: false
  }
};

export const fetchOrderListDetails = createAsyncThunk<OrderList, string, { rejectValue: string }>(
  'ordersListForm/fetchOrderListDetails',
  async (orderListId, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${orderListsApiPrefix}/${orderListId}`);
      const { data } = response;
      return data;
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      switch (status) {
        case UNAUTHORIZED:
        case FORBIDDEN:
          return rejectWithValue(i18n.t('orderList.you cannot update this product'));
        case UNPROCESSABLE_ENTITY:
          return rejectWithValue(i18n.t('oorderList fetch product'));
        case NOT_FOUND:
          return rejectWithValue(i18n.t('orderList.not found'));
      }

      return rejectWithValue(i18n.t('errors.something went wrong'));
    }
  }
);

export const approveOrderList = createAsyncThunk<OrderList, OrderListSave, { rejectValue: string }>(
  'ordersListForm/approveOrderList',
  async ({ id, orderList }, { dispatch, rejectWithValue }) => {
    try {
      await axiosInstance.put(`${orderListsApiPrefix}/${id}`, orderList);
      const response = await axiosInstance.post(`${orderListsApiPrefix}/${id}/approve`);
      const { data } = response;
      return data;
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      switch (status) {
        case FORBIDDEN:
        case UNAUTHORIZED:
          return rejectWithValue(i18n.t('ordersList.you cannot approve this order list'));
        case NOT_FOUND:
          return rejectWithValue(i18n.t('ordersList.not found'));
        default:
          return rejectWithValue(i18n.t('errors.something went wrong'));
      }
    }
  }
);

export const saveOrderList = createAsyncThunk<OrderList, OrderListSave, { rejectValue: string }>(
  'ordersListForm/saveorderList',
  async ({ id, orderList, history, detailsOnly }, { dispatch, rejectWithValue }) => {
    try {
      let response;
      if (id) {
        response = await axiosInstance.put(detailsOnly ?
          `${orderListsApiPrefix}/detailsonly/${id}` :
          `${orderListsApiPrefix}/${id}`,
          orderList);
      } else {
        response = await axiosInstance.post(orderListsApiPrefix, orderList);
      }
      const { data } = response;
      if (!id) {
        history?.replace(`/customers/details/${orderList.customer_id}/orderlist/${data.id}`);
      }
      return data;
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      switch (status) {
        case UNAUTHORIZED:
        case FORBIDDEN:
          return rejectWithValue(i18n.t('orderList.you cannot update this order list'));
        case UNPROCESSABLE_ENTITY:
          return rejectWithValue(i18n.t('orderList.cannot save order list'));
        case NOT_FOUND:
          return rejectWithValue(i18n.t('orderList.not found'));
      }

      return rejectWithValue(i18n.t('errors.something went wrong'));
    }
  }
);

const ordersListSlice = createSlice({
  initialState,
  name: 'ordersListForm',
  reducers: {
    closeError: state => {
      state.apiErrors = {};
    },
    updateListDelete: (state, { payload }: PayloadAction<{ id: string; value: boolean }>) => {
      const { id, value } = payload;
      if (value) {
        set(state, `loaders.deletingErrorItems.${id}`, '');
      }
      set(state, `loaders.isDeletingItems.${id}`, value);
    },
    resetDetails: state => initialState
  },
  extraReducers: builder =>
    builder
      .addCase(fetchOrderListDetails.fulfilled, (state, { payload }) => {
        state.loaders.isLoading = false;
        state.details = payload;
      })
      .addCase(fetchOrderListDetails.pending, state => {
        state.loaders.isLoading = true;
        state.apiErrors.loadingError = '';
      })
      .addCase(fetchOrderListDetails.rejected, (state, { payload }) => {
        state.loaders.isLoading = false;
        state.apiErrors.loadingError = payload || i18n.t('errors.something went wrong');
      })
      .addCase(saveOrderList.fulfilled, (state, { payload }) => {
        state.loaders.isSaving = false;
        state.details = payload;
      })
      .addCase(saveOrderList.pending, state => {
        state.loaders.isSaving = true;
        state.apiErrors.savingError = '';
      })
      .addCase(saveOrderList.rejected, (state, { payload }) => {
        state.loaders.isSaving = false;
        state.apiErrors.savingError = payload || i18n.t('errors.something went wrong');
      })
      .addCase(approveOrderList.fulfilled, (state, { payload }) => {
        state.loaders.isSaving = false;
        state.details = payload;
      })
      .addCase(approveOrderList.pending, state => {
        state.loaders.isSaving = true;
        state.apiErrors.savingError = '';
      })
      .addCase(approveOrderList.rejected, (state, { payload }) => {
        state.loaders.isSaving = false;
        state.apiErrors.savingError = payload || i18n.t('errors.something went wrong');
      })
});

const { actions, reducer } = ordersListSlice;
export { reducer as ordersListForm };
export const { closeError, resetDetails, updateListDelete } = actions;

export const getOrderListDetails = (state: RootState) => state.ordersListForm.details;
export const getOrdersListDetailsLoaders = (state: RootState) => state.ordersListForm.loaders;
export const getOrdersListDetailsApiErrors = (state: RootState) => state.ordersListForm.apiErrors;
